/** Single block id for Other Surface sticky-strike smile charts. */
export const OTHER_SURFACE_STRIKE_GRAPHS_BLOCK_ID =
  "other_surface_sticky_strike_graphs";

/** Query params for Other Surface index/load (snake + camel PCS id). */
export function buildOtherSurfaceQueryParams(params = {}) {
  if (!params || typeof params !== "object") return {};
  const pcs =
    params.product_contract_style_id ??
    params.productContractStyleId ??
    params.product_id;
  const { product_id: _omitProductId, ...rest } = params;
  if (pcs == null || pcs === "") return rest;
  return {
    ...rest,
    product_contract_style_id: pcs,
    productContractStyleId: pcs,
  };
}

/**
 * One backend graph entry → `block_graphs` item for VolSurfaceOptionsGraph.
 * @param {{ vol_smile_id?: number, tenor?: string, graph?: object }} entry
 */
export function graphEntryToBlockGraph(entry) {
  if (!entry || typeof entry !== "object") return null;
  const volSmileId = entry.vol_smile_id ?? entry.graph?.vol_smile_id;
  if (volSmileId == null) return null;
  const chart = entry.graph ?? entry;
  const graphs = Array.isArray(chart)
    ? chart
    : chart && typeof chart === "object"
      ? [chart]
      : [];
  if (graphs.length === 0) return null;
  return {
    id: volSmileId,
    graphs,
    tenor: entry.tenor,
    backend_data: { user_vol_smile_id: volSmileId },
  };
}

/** Build VolSurfaceOptionsGraph container shape from index/update `graphs`. */
export function buildStrikeGraphsVolSurfaceData(graphEntries) {
  const blockGraphs = (graphEntries ?? [])
    .map(graphEntryToBlockGraph)
    .filter(Boolean);
  if (blockGraphs.length === 0) return null;
  return [
    {
      block_id: OTHER_SURFACE_STRIKE_GRAPHS_BLOCK_ID,
      block_graphs: blockGraphs,
    },
  ];
}

/** Replace or insert charts keyed by `vol_smile_id`. */
export function mergeStrikeGraphEntries(existingVolSurfaceData, graphEntries) {
  const patchById = new Map();
  for (const entry of graphEntries ?? []) {
    const blockGraph = graphEntryToBlockGraph(entry);
    if (blockGraph) patchById.set(String(blockGraph.id), blockGraph);
  }
  if (patchById.size === 0) return existingVolSurfaceData;

  const prevBlocks = Array.isArray(existingVolSurfaceData)
    ? existingVolSurfaceData
    : [];
  const blockIndex = prevBlocks.findIndex(
    (block) => block?.block_id === OTHER_SURFACE_STRIKE_GRAPHS_BLOCK_ID
  );
  const prevBlock =
    blockIndex >= 0 ? prevBlocks[blockIndex] : { block_graphs: [] };
  const existingGraphs = prevBlock.block_graphs ?? [];
  const ordered = [];
  const seen = new Set();

  for (const graph of existingGraphs) {
    const id = String(graph?.id);
    if (patchById.has(id)) {
      ordered.push(patchById.get(id));
      seen.add(id);
    } else {
      ordered.push(graph);
      seen.add(id);
    }
  }
  for (const [id, graph] of patchById) {
    if (!seen.has(id)) ordered.push(graph);
  }

  const nextBlock = {
    ...prevBlock,
    block_id: OTHER_SURFACE_STRIKE_GRAPHS_BLOCK_ID,
    block_graphs: ordered,
  };

  if (blockIndex >= 0) {
    return prevBlocks.map((block, index) =>
      index === blockIndex ? nextBlock : block
    );
  }
  return [...prevBlocks, nextBlock];
}

/** `GET other-vol-surfaces`: table fields plus optional `graphs`. */
export function parseStickyStrikeIndexPayload(data) {
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return { stickyStrike: data, graphs: [] };
  }
  if (!Array.isArray(data.graphs)) {
    return { stickyStrike: data, graphs: [] };
  }
  const { graphs, ...stickyStrike } = data;
  return { stickyStrike, graphs };
}

function pickUpdateMeta(data) {
  if (!data || typeof data !== "object") return {};
  const {
    rows: _rows,
    graphs: _graphs,
    highlightedKeys,
    show_highlighted_message,
    highlightMessage,
    ...rest
  } = data;
  return {
    highlightedKeys,
    show_highlighted_message,
    highlightMessage,
    ...rest,
  };
}

// `rows` of a cell update: one row array per smile for a UL update,
// { rows, highlightedKeys, ... } for premiums, a single row for a strike.
function updateRowsList(rows) {
  if (Array.isArray(rows)) return rows.flat();
  if (Array.isArray(rows.rows)) return rows.rows;
  return rows.vol_smile_id != null ? [rows] : [];
}

/**
 * Update / add-empty-row success: `{ rows, graphs }`, not `data` as rows only.
 * Still accepts a legacy single row object or bare row array.
 */
export function parseStickyStrikeUpdatePayload(data) {
  if (Array.isArray(data)) {
    return { rows: data, graphs: [], meta: {} };
  }
  if (!data || typeof data !== "object") {
    return { rows: [], graphs: [], meta: {} };
  }
  if (data.rows && typeof data.rows === "object") {
    // A premium update nests its highlights next to its rows.
    const nested = Array.isArray(data.rows.rows) ? data.rows : null;
    return {
      rows: updateRowsList(data.rows),
      graphs: Array.isArray(data.graphs) ? data.graphs : [],
      meta: nested
        ? { ...pickUpdateMeta(data), ...pickUpdateMeta(nested) }
        : pickUpdateMeta(data),
    };
  }
  const looksLikeRow =
    data.vol_smile_id != null &&
    (data.strike != null ||
      data.premium != null ||
      data.vol != null ||
      data.key != null);
  if (looksLikeRow) {
    return {
      rows: [data],
      graphs: Array.isArray(data.graphs) ? data.graphs : [],
      meta: pickUpdateMeta(data),
    };
  }
  return { rows: [], graphs: [], meta: pickUpdateMeta(data) };
}

export function normalizeOtherSurfaceGraphPatch(otherSurfaceGraph) {
  if (!otherSurfaceGraph) return [];
  return Array.isArray(otherSurfaceGraph)
    ? otherSurfaceGraph
    : [otherSurfaceGraph];
}
